import { useEffect, useState, useRef, useCallback } from "react";
import { useSelector, useDispatch } from 'react-redux';
import {
  Input,
  Form,
  Upload,
  Button,
  Select,
  DatePicker,
  Radio,
  Tabs,
  message,
  Row,
  Col,
  List,
  Space,
  Typography,
} from "antd";
import moment from 'moment';
import { UploadOutlined } from '@ant-design/icons';
import { getLangList } from '@/lib/i18n';
import {
  getDataArray,
  getOptionArray,
  selectFilterOption,
  modalFromLayout,
  showDelConfirm,
} from "@/lib/antdElementFunction";
import { ReButton } from "@/components/refineElement";
import { avatarUploadProps, normFile, otherFilesUploadProps, convertBase64ToPicture } from "@/lib/uploadFunction";
import { openBlobDownloadDialog } from "@/lib/exlsDownload";
import { clearUserinfo } from "@/stores/user/action";
import DepartmentConfig from "@/config/department";
import UserConfig from "@/config/user";
import RoleConfig from "@/config/role";
import { DepartmentType, AgentType, RoleListType, RoleType, FileType, UserDetailsType } from './types';

const { getDepartmentInfo } = DepartmentConfig;
const {
  getUserAgentInfo,
  deleteUserAccountDetail,
  getUserFileList,
  uploadUserFile,
  deleteUserFile,
  downloadUserFile,
} = UserConfig;
const { getRoleInfo } = RoleConfig;
const { TabPane } = Tabs;
const { Text } = Typography;

type Props = {
  form: any;
  handleType: string;
  getUserInfo: Function;
}

function ModalProfileTeacher(props: Props) {
  const { form, handleType, getUserInfo } = props;
  const [departmentArray, setDepartmentArray] = useState<DepartmentType[]>([]);
  const [agentArray, setAgentArray] = useState<AgentType[]>([]);
  const [roleList, setRoleList] = useState<RoleListType[]>([]);
  const [fileArray, setFileArray] = useState<FileType[]>([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(0);
  const [panes, setPanes] = useState([]);
  const [activeKey, setActiveKey] = useState('0');
  const dispatch = useDispatch();
  const { userInfo = {} } = useSelector((state: any) => state.user);
  const { userAccountId, idCard, qrCode } = userInfo;
  const profileInfoT: any = getLangList("profileInfo");
  const buttonT: any = getLangList("button");
  const errorT: any = getLangList("error");
  const errorCodeT: any = getLangList("errorCode");
  const isCreateUser = handleType === 'createUser';
  const roleData = roleList
    ?.find((item: RoleListType) => item.departmentId === selectedDepartmentId)?.roleList
    ?.map((item: RoleType) => ({ label: item.roleDesc, value: item.roleId })) ?? [];
  const tabIndex = useRef(-1);
  const isFileMax = fileArray.length >= 5;
  const isViewProfileInfo = handleType === 'viewProfileInfo';
  const filterDepartmentArray = departmentArray.filter((item: any) => item.id !== 1); // 過濾掉學生部門

  const onChangeDepartment = useCallback((value: number) => {
    form.setFieldsValue({ roleId: '' });
    setSelectedDepartmentId(value);
  }, [form]);

  useEffect(() => {
    if (Object.keys(userInfo).length > 0) {
      const {
        nameCn = '',
        nameEn = '',
        departmentId = '',
        roleId = '',
        extension = '',
        cellPhoneNumber = '',
        businessEmail = '',
        personalEmail = '',
        accessCardNumber = '',
        agentDepartmentId = '',
        agentUserAccountId = '',
        dueDate,
        terminationDate,
        birthday,
        gender = '',
        identityNumber = '',
        details = [],
        userAccountId,
      } = userInfo;

      onChangeDepartment(departmentId);
      form.setFieldsValue({
        nameCn,
        nameEn,
        departmentId,
        roleId,
        extension,
        cellPhoneNumber,
        businessEmail,
        personalEmail,
        accessCardNumber,
        agentDepartmentId,
        agentUserAccountId,
        dueDate: dueDate ? moment(dueDate) : undefined,
        terminationDate: terminationDate ? moment(terminationDate) : undefined,
        birthday: birthday ? moment(birthday) : undefined,
        gender,
        identityNumber,
      });
      details.forEach((item: UserDetailsType, index: number) => {
        item.key = `${index}`;
        form.setFieldsValue({
          [`name_${index}`]: item.name,
          [`relationship_${index}`]: item.relationship,
          [`homePhone_${index}`]: item.homePhone,
          [`officePhone_${index}`]: item.officePhone,
          [`cellphone_${index}`]: item.cellphone,
          [`email_${index}`]: item.email,
          [`userAccountDetailId_${index}`]: item.userAccountDetailId,
          tabIndex: ++tabIndex.current,
        });
      });
      setPanes(details);
      getUserFileListFunction(userAccountId);
    }
  }, [form, dispatch, userInfo, onChangeDepartment]);

  useEffect(() => {
    getDepartmentInfoFunction();
    getRoleInfoFunction();
  }, []);

  useEffect(() => {
    return () => {
      dispatch(clearUserinfo());
    };
  }, [dispatch]);

  function getDepartmentInfoFunction() {
    getDepartmentInfo().then((res: any) => {
      const departmentArray = res?.value ?? [];
      setDepartmentArray(departmentArray);
    });
  };

  function getUserAgentFunction(value: number) {
    const params = { departmentId: value };
    getUserAgentInfo(params).then((res: any) => {
      const agentArray = res?.value ?? [];
      setAgentArray(agentArray);
    });
  };

  function getRoleInfoFunction() {
    getRoleInfo().then((res: any) => {
      const roleList = res?.value ?? [];
      setRoleList(roleList);
    });
  };

  const clickUserFileDelete = (data: FileType) => () => {
    showDelConfirm(() => deleteUserFileFunction(data));
  };

  function getUserFileListFunction(userAccountId: number) {
    getUserFileList({ userAccountId }).then((res: any) => {
      const fileArray = res?.value ?? [];
      setFileArray(fileArray);
    });
  };

  function uploadUserFileFunction(e: any) {
    const { file, onSuccess, onError } = e;
    const params = { urlParams: { userAccountId }, fileFormat: { file } };
    const isTooLarge = file.size / 1024 / 1024 > 5;

    if (isTooLarge) {
      message.error(errorT.IMAGE_TOO_LARGE);
      return onError();
    }

    uploadUserFile(params).then((res: any) => {
      if (!res.success) {
        message.error(errorCodeT[res.errorCode] ?? errorT.failed);
        return onError();
      }
      onSuccess();
      getUserFileListFunction(userAccountId);
    });
  }

  function deleteUserFileFunction(file: FileType) {
    const { id } = file;

    deleteUserFile(`${id}`).then((res: any) => {
      if (!res.success) {
        message.error(errorT.failed);
        return false
      }
      message.success(errorT.success);
      getUserFileListFunction(userAccountId);
      return true;
    });
  };

  const downloadUserFileFunction = (file: FileType) => () => {
    const { id, fileName } = file;

    downloadUserFile(`${id}`).then((res: any) => {
      openBlobDownloadDialog(res, fileName);
    });
  };

  function getFormatAgentData() {
    return agentArray.map((item: AgentType) => ({ label: `${item.nameCn}-${item.nameEn}`, value: item.userAccountId }));
  };

  function deleteUserAccountDetailFunction(targetKey: string) {
    const newPanes = panes.filter((item: any) => item.key !== targetKey);
    const userAccountDetailId = form.getFieldValue(`userAccountDetailId_${targetKey}`);

    if (userAccountDetailId) {
      deleteUserAccountDetail(userAccountDetailId).then((res: any) => {
        if (!res.success) {
          return message.error(errorT.failed);
        }
        getUserInfo();
      });
    }
    form.setFieldsValue({ tabIndex: --tabIndex.current });
    setPanes(newPanes);
    setActiveKey('0');
  };

  function onChangeAgentDepartment(value: number) {
    form.setFieldsValue({ agentUserAccountId: '' });
    getUserAgentFunction(value);
  };

  function addTab() {
    const index = ++tabIndex.current;
    const newPanes: any = [...panes];

    form.setFieldsValue({
      [`name_${index}`]: '',
      [`relationship_${index}`]: 'relationship',
      [`homePhone_${index}`]: '',
      [`officePhone_${index}`]: '',
      [`cellphone_${index}`]: '',
      [`email_${index}`]: '',
      tabIndex: index,
    });
    newPanes.push({ relationship: 'relationship', key: `${index}` });
    setPanes(newPanes);
    setActiveKey(`${index}`);
  };

  function onEdit(targetKey: any, action: any) {
    action === 'add' && addTab();
    action === 'remove' && showDelConfirm(() => deleteUserAccountDetailFunction(targetKey), buttonT.confirm_delete);
  };

  function renderParent() {
    return panes.map((item: any, index: number) => {
      const { relationship = '' } = item;

      return (
        <TabPane
          tab={relationship}
          key={`${index}`}
          closable={(index === tabIndex.current) && (index !== 0) && !isViewProfileInfo}
          forceRender
        >
          <Form.Item label={profileInfoT.NAME} name={`name_${index}`}>
            <Input />
          </Form.Item>
          <Form.Item label={profileInfoT.RELATIONSHIP} name={`relationship_${index}`}>
            <Input />
          </Form.Item>
          <Form.Item label={profileInfoT.HONE_PHONE} name={`homePhone_${index}`}>
            <Input />
          </Form.Item>
          <Form.Item label={profileInfoT.OFFICE_PHONE} name={`officePhone_${index}`}>
            <Input />
          </Form.Item>
          <Form.Item label={profileInfoT.CELLPHONE} name={`cellphone_${index}`}>
            <Input />
          </Form.Item>
          <Form.Item label={profileInfoT.EMAIL} name={`email_${index}`}>
            <Input />
          </Form.Item>
          <Form.Item style={{ display: 'none' }} name={`userAccountDetailId_${index}`}>
            <Input />
          </Form.Item>
        </TabPane>
      );
    });
  };

  return (
    <Form {...modalFromLayout} form={form}>
      {isCreateUser &&
        <Form.Item label={profileInfoT.ACCOUNT} name="username" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
      }

      {isCreateUser &&
        <Form.Item label={profileInfoT.PASSWORD} name="password" rules={[{ required: true }]}>
          <Input.Password />
        </Form.Item>
      }

      <Form.Item label={profileInfoT.NAME_CN} name="nameCn" rules={[{ required: true }]}>
        <Input />
      </Form.Item>

      <Form.Item label={profileInfoT.NAME_EN} name="nameEn" rules={[{ required: true }]}>
        <Input />
      </Form.Item>

      <Form.Item
        label={profileInfoT.DEPARTMENT}
        name="departmentId"
        rules={[{ required: true }]}
      >
        <Select
          showSearch
          style={{ width: "100%" }}
          optionFilterProp="children"
          filterOption={(input: any, option: any) => selectFilterOption(input, option)}
          onChange={onChangeDepartment}
        >
          {getOptionArray(getDataArray(filterDepartmentArray, 'id', 'departmentName'))}
        </Select>
      </Form.Item>

      <Form.Item
        label={profileInfoT.ROLE}
        name="roleId"
        rules={[{ required: true }]}
      >
        <Select
          showSearch
          style={{ width: "100%" }}
          optionFilterProp="children"
          filterOption={(input: any, option: any) => selectFilterOption(input, option)}
        >
          {getOptionArray(roleData)}
        </Select>
      </Form.Item>

      <Form.Item label={profileInfoT.EXTENSION} name="extension">
        <Input />
      </Form.Item>

      <Form.Item label={profileInfoT.CELLPHONE} name="cellPhoneNumber">
        <Input />
      </Form.Item>

      <Form.Item label={profileInfoT.BUSINESS_EMAIL} name="businessEmail">
        <Input />
      </Form.Item>

      <Form.Item label={profileInfoT.PERSONAL_EMAIL} name="personalEmail">
        <Input />
      </Form.Item>

      <Form.Item label={profileInfoT.ACCESS_CARD_NUMBER} name="accessCardNumber">
        <Input />
      </Form.Item>

      <Form.Item label={`${profileInfoT.AGENT}${profileInfoT.DEPARTMENT}`} name="agentDepartmentId">
        <Select
          showSearch
          style={{ width: "100%" }}
          optionFilterProp="children"
          filterOption={(input: any, option: any) => selectFilterOption(input, option)}
          onChange={onChangeAgentDepartment}
        >
          {getOptionArray(getDataArray(filterDepartmentArray, 'id', 'departmentName'))}
        </Select>
      </Form.Item>

      <Form.Item label={`${profileInfoT.AGENT}${profileInfoT.NAME}`} name="agentUserAccountId">
        <Select
          showSearch
          style={{ width: "100%" }}
          optionFilterProp="children"
          filterOption={(input: any, option: any) => selectFilterOption(input, option)}
        >
          {getOptionArray(getFormatAgentData())}
        </Select>
      </Form.Item>

      <Form.Item label={profileInfoT.DUE_DATE} name="dueDate" rules={[{ required: true }]}>
        <DatePicker allowClear={false} />
      </Form.Item>

      <Form.Item label={profileInfoT.TERMINATION_DATE} name="terminationDate">
        <DatePicker allowClear={false} />
      </Form.Item>

      <Form.Item label={profileInfoT.BIRTHDAY} name="birthday">
        <DatePicker allowClear={false} />
      </Form.Item>

      <Form.Item label={profileInfoT.GENDER} name="gender">
        <Radio.Group>
          <Radio value="male">{profileInfoT.MALE}</Radio>
          <Radio value="female">{profileInfoT.FEMALE}</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item label={profileInfoT.IDENTITY_NUMBER} name="identityNumber">
        <Input />
      </Form.Item>

      <Form.Item
        label={profileInfoT.AVATAR}
        name="idCardImg"
        valuePropName="fileList"
        getValueFromEvent={normFile}
        tooltip={idCard ?
          <Row justify="center">
            <Col>Previous:</Col>
            <Col><img height="200px" width="200px" alt="avatar" src={convertBase64ToPicture(idCard)} /></Col>
          </Row> : null
        }
      >
        <Upload {...avatarUploadProps}>
          <Button icon={<UploadOutlined />}>Click to Upload</Button>
        </Upload>
      </Form.Item>

      <Form.Item
        label={profileInfoT.QRCODE}
        name="qrCode"
        valuePropName="fileList"
        getValueFromEvent={normFile}
        tooltip={qrCode ?
          <Row justify="center">
            <Col>Previous:</Col>
            <Col><img height="200px" width="200px" alt="avatar" src={convertBase64ToPicture(qrCode)} /></Col>
          </Row> : null
        }
      >
        <Upload {...avatarUploadProps}>
          <Button icon={<UploadOutlined />}>Click to Upload</Button>
        </Upload>
      </Form.Item>

      <Form.Item
        label={profileInfoT.OTHER_DOCUMENT}
        name="otherFiles"
        valuePropName="fileList"
        getValueFromEvent={normFile}
        help={isFileMax ? <Text type="danger">{errorT.MORE_THAN_MAX_FILE}</Text> : null}
        tooltip={{
          title: (
            <Row justify="center">
              <Col>Now Files:</Col>
              <Col span={24}>
                <List
                  className="modal-profile-teacher-list"
                  size="small"
                  dataSource={fileArray}
                  renderItem={(item: FileType) => (
                    <List.Item>
                      <Space>
                        <ReButton
                          iconName='download'
                          onClick={downloadUserFileFunction(item)}
                          attributeObject={{ size: "small" }}
                        />
                        <ReButton
                          iconName='delete'
                          onClick={clickUserFileDelete(item)}
                          attributeObject={{ type: 'danger', size: "small" }}
                        />
                        {item.fileName}
                      </Space>
                    </List.Item>
                  )}
                />
              </Col>
            </Row>
          ),
        }}
      >
        <Upload
          {...otherFilesUploadProps}
          customRequest={uploadUserFileFunction}
          onRemove={deleteUserFileFunction}
          disabled={isFileMax}
        >
          <Button icon={<UploadOutlined />}>Click to Upload</Button>
        </Upload>
      </Form.Item>

      <Form.Item style={{ display: 'none' }} name="tabIndex">
        <Input />
      </Form.Item>

      <Tabs
        defaultActiveKey="0"
        type="editable-card"
        onEdit={onEdit}
        onChange={(activeKey: string) => setActiveKey(activeKey)}
        activeKey={activeKey}
        hideAdd={isViewProfileInfo}
        addIcon={<span>+ {profileInfoT.CONTACT}</span>}
      >
        {renderParent()}
      </Tabs>
    </Form>
  );
}

export default ModalProfileTeacher;